import React, { useState, useCallback, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  TrashIcon,
  BankIcon,
  PlusIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import { useGetIntegration } from 'src/api/rpc/hooks/integrations/useIntegrations';
import { orpc } from 'src/api/rpc/client';
import { useMutation } from '@tanstack/react-query';
import {
  IntegrationAvailability,
  IntegrationKey,
  PlaidBankAccount,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnExit,
} from 'react-plaid-link';
import { formatDateTime } from '@erp/shared-utils';
import { T, Var, Num, useGT } from 'gt-react';

export function PlaidIntegrationSettings({
  availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const isAvailable = availability?.available ?? true;
  const unavailableReason = availability?.reason;
  const { showToast } = useToast();
  const gt = useGT();
  const plaidUnavailableFallback = useMemo(
    () =>
      gt('Plaid is not available right now.', {
        context: 'Plaid integration generic unavailable message',
      }),
    [gt],
  );
  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetIntegration(IntegrationKey.Plaid);

  // Plaid-specific mutations defined in component
  const createLinkToken = useMutation(
    orpc.integrations.plaid.createLinkToken.mutationOptions(),
  );
  const exchangePublicToken = useMutation(
    orpc.integrations.plaid.exchangePublicToken.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const deleteBankAccount = useMutation(
    orpc.integrations.plaid.deleteBankAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const createUpdateLinkToken = useMutation(
    orpc.integrations.plaid.createUpdateLinkToken.mutationOptions(),
  );

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isInitializingLink, setIsInitializingLink] = useState(false);
  const [reauthenticatingAccountId, setReauthenticatingAccountId] = useState<
    string | null
  >(null);

  // Initialize Plaid Link
  const handleInitializePlaidLink = async () => {
    if (!isAvailable) {
      showToast({
        title: gt('Integration Unavailable', {
          context: 'Plaid toast title integration unavailable',
        }),
        message: unavailableReason ?? plaidUnavailableFallback,
        color: 'warning',
        icon: 'warning',
      });
      return;
    }

    setIsInitializingLink(true);
    try {
      const result = await createLinkToken.mutateAsync(undefined);
      setLinkToken(result.linkToken);
    } catch (error) {
      showToast({
        title: gt('Failed to Initialize', {
          context: 'Plaid toast title failed to initialize link',
        }),
        message: gt('Unable to initialize Plaid Link. Please try again.', {
          context: 'Plaid toast message initialize link failed',
        }),
        color: 'error',
        icon: 'error',
      });
      setIsInitializingLink(false);
    }
  };

  // Plaid Link success handler
  const onSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      console.info('[PlaidLink] Link success', {
        linkSessionId: metadata.link_session_id,
        accountId: metadata.accounts.map((acc) => acc.id),
        institutionId: metadata.institution?.institution_id,
      });

      try {
        // If we're re-authenticating, just refetch the integration
        // The access token is automatically updated by Plaid
        if (reauthenticatingAccountId) {
          await refetch();
          const institutionName =
            metadata.institution?.name ??
            gt('your bank account', {
              context: 'Plaid generic bank account name fallback',
            });
          showToast({
            title: gt('Re-authentication Successful', {
              context: 'Plaid toast title re-auth success',
            }),
            message: gt('Successfully re-authenticated {institutionName}.', {
              context: 'Plaid toast message re-auth success with institution',
              institutionName,
            }),
            color: 'success',
            icon: 'check',
          });
        } else {
          // Normal connection flow
          await exchangePublicToken.mutateAsync({
            publicToken,
            institutionName:
              metadata.institution?.name ??
              gt('Unknown Bank', {
                context: 'Plaid unknown institution fallback for API',
              }),
            institutionId: metadata.institution?.institution_id || '',
            accountIds: metadata.accounts.map((acc) => acc.id),
          });

          const institutionName =
            metadata.institution?.name ??
            gt('your bank account', {
              context: 'Plaid generic bank account name fallback',
            });
          showToast({
            title: gt('Bank Account Connected', {
              context: 'Plaid toast title bank connected',
            }),
            message: gt('Successfully connected {institutionName}.', {
              context: 'Plaid toast message bank connected with institution',
              institutionName,
            }),
            color: 'success',
            icon: 'check',
          });
        }

        // Clear link token after successful connection
        setLinkToken(null);
      } catch (error) {
        showToast({
          title: reauthenticatingAccountId
            ? gt('Re-authentication Failed', {
                context: 'Plaid toast title re-auth failed',
              })
            : gt('Connection Failed', {
                context: 'Plaid toast title connection failed',
              }),
          message: reauthenticatingAccountId
            ? gt('Failed to re-authenticate bank account. Please try again.', {
                context: 'Plaid toast message re-auth failed',
              })
            : gt('Failed to save bank account connection. Please try again.', {
                context: 'Plaid toast message save connection failed',
              }),
          color: 'error',
          icon: 'error',
        });
      } finally {
        setIsInitializingLink(false);
        setReauthenticatingAccountId(null);
      }
    },
    [exchangePublicToken, showToast, reauthenticatingAccountId, refetch, gt],
  );

  // Plaid Link exit handler
  const onExit: PlaidLinkOnExit = useCallback(
    (error, metadata) => {
      if (error) {
        console.error('[PlaidLink] Link error', {
          linkSessionId: metadata.link_session_id,
          errorCode: error.error_code,
          errorMessage: error.error_message,
          displayMessage: error.display_message,
        });
        showToast({
          title: reauthenticatingAccountId
            ? gt('Re-authentication Cancelled', {
                context: 'Plaid toast title re-auth cancelled',
              })
            : gt('Connection Cancelled', {
                context: 'Plaid toast title connection cancelled',
              }),
          message:
            error.display_message ||
            (reauthenticatingAccountId
              ? gt('Bank account re-authentication was cancelled.', {
                  context: 'Plaid toast message re-auth cancelled',
                })
              : gt('Bank account connection was cancelled.', {
                  context: 'Plaid toast message connection cancelled',
                })),
          color: 'warning',
          icon: 'warning',
        });
      } else {
        console.info('[PlaidLink] Link exited', {
          linkSessionId: metadata.link_session_id,
        });
      }
      setLinkToken(null);
      setIsInitializingLink(false);
      setReauthenticatingAccountId(null);
    },
    [showToast, reauthenticatingAccountId, gt],
  );

  const { open: openPlaidLink, ready: plaidLinkReady } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Handle "Connect Bank Account" button click
  const handleConnectBankAccount = async () => {
    await handleInitializePlaidLink();
  };

  // When link token is set and Plaid Link is ready, open it automatically
  React.useEffect(() => {
    if (linkToken && plaidLinkReady) {
      openPlaidLink();
    }
  }, [linkToken, plaidLinkReady, openPlaidLink]);

  // Handle re-authenticate bank account
  const handleReauthenticateAccount = useCallback(
    async (accountId: string) => {
      if (!isAvailable) {
        showToast({
          title: gt('Integration Unavailable', {
            context: 'Plaid toast title integration unavailable',
          }),
          message: unavailableReason ?? plaidUnavailableFallback,
          color: 'warning',
          icon: 'warning',
        });
        return;
      }

      setReauthenticatingAccountId(accountId);
      try {
        const result = await createUpdateLinkToken.mutateAsync({ accountId });
        setLinkToken(result.linkToken);
      } catch (error) {
        showToast({
          title: gt('Failed to Initialize', {
            context: 'Plaid toast title failed to initialize re-auth',
          }),
          message: gt(
            'Unable to initialize re-authentication. Please try again.',
            {
              context: 'Plaid toast message re-auth initialize failed',
            },
          ),
          color: 'error',
          icon: 'error',
        });
        setReauthenticatingAccountId(null);
      }
    },
    [
      createUpdateLinkToken,
      isAvailable,
      unavailableReason,
      showToast,
      gt,
      plaidUnavailableFallback,
    ],
  );

  // Handle delete bank account
  const handleDeleteAccount = async (accountId: string) => {
    if (
      !window.confirm(
        gt(
          'Are you sure you want to disconnect this bank account? This action cannot be undone.',
          {
            context: 'Plaid confirm disconnect bank account',
          },
        ),
      )
    ) {
      return;
    }

    try {
      await deleteBankAccount.mutateAsync({ accountId });

      showToast({
        title: gt('Account Disconnected', {
          context: 'Plaid toast title account disconnected',
        }),
        message: gt('Bank account has been successfully disconnected.', {
          context: 'Plaid toast message account disconnected',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: gt('Disconnection Failed', {
          context: 'Plaid toast title disconnect failed',
        }),
        message: gt('Failed to disconnect bank account. Please try again.', {
          context: 'Plaid toast message disconnect failed',
        }),
        color: 'error',
        icon: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="Plaid integration settings loading label">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="Plaid integration settings load error message">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  const accounts =
    integration?.key === IntegrationKey.Plaid
      ? integration.settings.accounts
      : [];
  const hasAccounts = accounts.length > 0;
  const connectedAccountCount = accounts.length;

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="font-medium">
              <T context="Plaid integration status active label">
                Integration Active
              </T>
            </span>
          </div>
          <Badge
            variant="outline"
            className="bg-green-100 text-green-700 border-green-300"
          >
            {hasAccounts ? (
              connectedAccountCount === 1 ? (
                <T context="Plaid connected accounts badge singular">
                  <Num>{connectedAccountCount}</Num> Account Connected
                </T>
              ) : (
                <T context="Plaid connected accounts badge plural">
                  <Num>{connectedAccountCount}</Num> Accounts Connected
                </T>
              )
            ) : (
              <T context="Plaid ready to connect badge">Ready to Connect</T>
            )}
          </Badge>
          {!isAvailable && (
            <Badge
              variant="outline"
              className="border-red-300 text-red-700 bg-red-50"
            >
              <T context="Plaid integration unavailable badge">Unavailable</T>
            </Badge>
          )}
        </div>
        {!isAvailable && (
          <p className="text-sm text-red-600 mt-2">
            {unavailableReason ?? plaidUnavailableFallback}
          </p>
        )}
      </div>

      <Separator />

      {/* Connected Bank Accounts */}
      {hasAccounts ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              <T context="Plaid connected bank accounts card title">
                Connected Bank Accounts
              </T>
            </CardTitle>
            <CardDescription>
              <T context="Plaid connected bank accounts card description">
                Manage your connected bank accounts
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {accounts.map((account: PlaidBankAccount) => (
              <Card key={account.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <BankIcon className="h-5 w-5 text-blue-700" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div>
                          <h4 className="font-semibold text-base">
                            {account.institutionName}
                          </h4>
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">
                            <T context="Plaid linked account count label prefix">
                              Accounts:{' '}
                            </T>
                          </span>
                          <span className="text-gray-600">
                            {account.accountIds.length === 1 ? (
                              <T context="Plaid linked account count singular">
                                <Num>{account.accountIds.length}</Num> account
                              </T>
                            ) : (
                              <T context="Plaid linked account count plural">
                                <Num>{account.accountIds.length}</Num> accounts
                              </T>
                            )}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <T context="Plaid account connected timestamp line">
                            Connected on{' '}
                            <Var name="date">
                              {formatDateTime(account.connectedAt)}
                            </Var>
                          </T>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReauthenticateAccount(account.id)}
                        disabled={
                          !isAvailable ||
                          reauthenticatingAccountId === account.id ||
                          createUpdateLinkToken.isPending
                        }
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title={gt('Re-authenticate this bank account', {
                          context:
                            'Plaid re-authenticate bank icon button title',
                        })}
                      >
                        {reauthenticatingAccountId === account.id ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <ArrowsClockwiseIcon className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAccount(account.id)}
                        disabled={deleteBankAccount.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deleteBankAccount.isPending ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Connect Another Account Button */}
            <div className="pt-2">
              <Button
                onClick={handleConnectBankAccount}
                disabled={isInitializingLink || !isAvailable}
                variant="outline"
                className="w-full"
              >
                {isInitializingLink ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    <T context="Plaid link initializing button label">
                      Initializing...
                    </T>
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    <T context="Plaid connect another bank account button">
                      Connect Another Bank Account
                    </T>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* No Accounts Connected - Initial Setup */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              <T context="Plaid empty state first bank account card title">
                Connect Your First Bank Account
              </T>
            </CardTitle>
            <CardDescription>
              <T context="Plaid empty state first bank account card description">
                Securely connect a bank account through Plaid for transaction
                sync, balance visibility, and reconciliation
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <BankIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                <T context="Plaid empty state no accounts heading">
                  No bank accounts connected
                </T>
              </h3>
              <p className="text-gray-600 mb-3">
                <T context="Plaid empty state body">
                  Connect your bank account to start syncing transactions and
                  supporting automated reconciliation workflows.
                </T>
              </p>
              {!isAvailable && (
                <p className="text-sm text-red-600 mb-4">
                  {unavailableReason ?? plaidUnavailableFallback}
                </p>
              )}
              <Button
                size="lg"
                className="px-8"
                onClick={handleConnectBankAccount}
                disabled={isInitializingLink || !isAvailable}
              >
                {isInitializingLink ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                    <T context="Plaid link initializing button label empty state">
                      Initializing...
                    </T>
                  </>
                ) : (
                  <>
                    <BankIcon className="h-4 w-4 mr-2" />
                    <T context="Plaid connect bank account primary button">
                      Connect Bank Account
                    </T>
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
